import { useState, useEffect, useRef } from "react";
import { api } from "../lib/utils";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import MaintenancePanel, { useMaintenanceSelection } from "../components/MaintenancePanel";
import { Building2, Plus, Pencil, Globe, MapPin, Upload, X, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Institutions() {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { selectedItems, selectMode, clearSelection } = useMaintenanceSelection();
  const [formData, setFormData] = useState({
    name: "",
    short_name: "",
    logo_url: "",
    address: "",
    website: "",
    approval_unit: "",
    report_footer: "",
    signature_label: "",
    authorized_officer: "",
  });

  useEffect(() => {
    fetchInstitutions();
  }, []);

  const fetchInstitutions = async () => {
    try {
      const response = await api.get("/institutions");
      setInstitutions(response.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const response = await api.post("/upload-logo", uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData({ ...formData, logo_url: response.data.logo_url });
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/institutions/${editingId}`, formData);
        toast.success("Institution updated");
      } else {
        await api.post("/institutions", formData);
        toast.success("Institution created");
      }
      setDialogOpen(false);
      resetForm();
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (institution) => {
    setEditingId(institution.institution_id);
    setFormData({
      name: institution.name || "",
      short_name: institution.short_name || "",
      logo_url: institution.logo_url || "",
      address: institution.address || "",
      website: institution.website || "",
      approval_unit: institution.approval_unit || "",
      report_footer: institution.report_footer || "",
      signature_label: institution.signature_label || "",
      authorized_officer: institution.authorized_officer || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: "",
      short_name: "",
      logo_url: "",
      address: "",
      website: "",
      approval_unit: "",
      report_footer: "",
      signature_label: "",
      authorized_officer: "",
    });
  };

  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;
    if (logoUrl.startsWith("http")) return logoUrl;
    return `${BACKEND_URL}${logoUrl}`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="institutions-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Awarding Institutions</h1>
            <p className="text-slate-500 mt-1">Manage universities and colleges for RPL awards</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-institution-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Institution
              </Button>
            </DialogTrigger>
            <MaintenancePanel
              entityType="institutions"
              entityLabel="Institutions"
              items={institutions}
              idField="institution_id"
              onRefresh={() => {
                fetchInstitutions();
                clearSelection();
              }}
              getItemLabel={(item) => item.name}
            />
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="institution-dialog-desc">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Institution" : "Create Institution"}</DialogTitle>
                <DialogDescription id="institution-dialog-desc">
                  Fill in the institution details below.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Institution Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Wilmington Metropolitan University"
                      required
                      data-testid="institution-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Short Name *</Label>
                    <Input
                      value={formData.short_name}
                      onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                      placeholder="e.g., WMU"
                      required
                      data-testid="institution-short-name-input"
                    />
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label>Institution Logo</Label>
                  <div className="flex items-start gap-4">
                    {formData.logo_url ? (
                      <div className="relative w-24 h-24 border rounded-lg overflow-hidden bg-slate-50">
                        <img
                          src={getLogoUrl(formData.logo_url)}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, logo_url: "" })}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        {uploading ? (
                          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-slate-400" />
                            <span className="text-xs text-slate-500 mt-1">Upload</span>
                          </>
                        )}
                      </div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                      data-testid="institution-logo-file-input"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-slate-500 mb-2">
                        Upload a logo image (PNG, JPG, SVG)
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="upload-logo-btn"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Choose File
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://..."
                      data-testid="institution-website-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Approval Unit</Label>
                    <Input
                      value={formData.approval_unit}
                      onChange={(e) => setFormData({ ...formData, approval_unit: e.target.value })}
                      placeholder="e.g., Academic Board"
                      data-testid="institution-approval-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Full institution address"
                    data-testid="institution-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Authorized Officer</Label>
                  <Input
                    value={formData.authorized_officer}
                    onChange={(e) => setFormData({ ...formData, authorized_officer: e.target.value })}
                    placeholder="e.g., Vice Chancellor"
                    data-testid="institution-officer-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Report Footer</Label>
                  <Textarea
                    value={formData.report_footer}
                    onChange={(e) => setFormData({ ...formData, report_footer: e.target.value })}
                    placeholder="Footer text for generated reports"
                    data-testid="institution-footer-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-institution-btn">
                    {editingId ? "Update" : "Create"} Institution
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-slate-100 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : institutions.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {institutions.map((inst) => (
              <Card
                key={inst.institution_id}
                className="border-slate-200 hover:shadow-md transition-shadow"
                data-testid={`institution-card-${inst.institution_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    {inst.logo_url ? (
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center">
                        <img
                          src={getLogoUrl(inst.logo_url)}
                          alt={inst.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(inst)} data-testid={`edit-institution-${inst.institution_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{inst.name}</h3>
                  <Badge variant="outline" className="mb-3">{inst.short_name}</Badge>
                  {inst.website && (
                    <a
                      href={inst.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                    >
                      <Globe className="w-3 h-3" />
                      Website
                    </a>
                  )}
                  {inst.address && (
                    <p className="flex items-start gap-1 text-sm text-slate-500 mt-2">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{inst.address}</span>
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Institutions Yet</h3>
              <p className="text-slate-500 mb-4">Add your first awarding institution to get started</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-institution-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Institution
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
